/**
 * Error Handler
 * Centralized error handling for the application
 */
import * as Sentry from '@sentry/react';
import logger from './logger';

class ErrorHandler {
  /**
   * Log error to console and external service
   * @param {Error} error
   * @param {string} context
   * @param {object} extra - Additional context data
   */
  static logError(error, context = 'Unknown', extra = {}) {
    logger.error(`[${context}] Error:`, error);

    // Send to Sentry in production
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error, {
        tags: {
          context,
          source: 'error_handler',
        },
        extra,
      });
    }
  }

  /**
   * Handle Auth0 errors
   * @param {Error} error - Auth0 error object
   */
  static handleAuthError(error) {
    logger.error('Auth0 Error:', error);

    // Send to Sentry with auth context
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error, {
        tags: {
          category: 'auth',
          source: 'auth0',
          errorCode: error.error,
        },
        extra: {
          errorDescription: error.error_description,
        },
      });
    }

    // Handle specific Auth0 errors
    if (error.error === 'access_denied') {
      logger.debug('Access denied - redirecting to home');
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - API error object
   */
  static handleApiError(error) {
    logger.error('API Error:', error);

    // Send to Sentry with API context
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error, {
        tags: {
          category: 'api',
          source: 'api_call',
          statusCode: error.response?.status?.toString(),
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          responseData: error.response?.data,
        },
      });
    }

    if (error.response?.status === 401) {
      // Handle unauthorized
    } else if (error.response?.status === 500) {
      // Handle server errors
    }
  }

  /**
   * Set user context for error tracking
   * @param {object} user - User info (id, email)
   */
  static setUserContext(user) {
    if (process.env.NODE_ENV === 'production' && user) {
      Sentry.setUser({
        id: user.sub || user.id,
        email: user.email,
      });
    }
  }

  /**
   * Clear user context (on logout)
   */
  static clearUserContext() {
    if (process.env.NODE_ENV === 'production') {
      Sentry.setUser(null);
    }
  }
}

export default ErrorHandler;
